/**
 * Navigation data (URL-parity with the old web app).
 *
 * Each item carries a `lucide-react` icon component (type-safe — no string
 * lookup) and, where relevant, a `badge` key that maps onto the counts returned
 * by `useCounts()`. `action` items (e.g. Logout) are handled by the consuming
 * component rather than rendered as plain links.
 *
 * NOTE: lucide-react removed its brand icons (Instagram/Facebook/YouTube), so
 * the footer socials use the closest generic glyphs as placeholders.
 */
import {
  LayoutGrid,
  Mail,
  Bell,
  PenSquare,
  Users,
  UserCircle,
  UserPlus,
  MapPin,
  Pencil,
  Settings,
  LogOut,
  Newspaper,
  Camera,
  Globe,
  PlayCircle,
  Search,
  type LucideIcon,
} from 'lucide-react';

/** Badge keys correspond to fields returned by `useCounts()`. */
export type BadgeKey =
  | 'friends'
  | 'requests'
  | 'notifications'
  | 'pokes'
  | 'messages'
  | 'visitors';

/** A navigable nav entry (renders as a link to `path`). */
export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  /** Badge count key, if this item shows an unread/pending badge. */
  badge?: BadgeKey;
}

/** A user-menu / action entry — either a link (`path`) or an action. */
export interface ActionItem {
  label: string;
  icon: LucideIcon;
  /** Destination route for link items. */
  path?: string;
  /** Named action handled by the consumer (e.g. `'logout'`). */
  action?: 'logout';
}

/** A footer text link (may point to a legacy `.html` URL). */
export interface FooterLink {
  label: string;
  path: string;
}

/** A footer social link with an icon. */
export interface SocialLink {
  label: string;
  icon: LucideIcon;
  href: string;
}

/** A mobile bottom-bar slot. All slots are now plain links — the raised
 *  center "Create" variant was removed 2026-06-08 when the plus button was
 *  replaced with a Search link. */
export interface MobileNavItem {
  label: string;
  icon: LucideIcon;
  path?: string;
  badge?: BadgeKey;
  /** Render the current user's avatar instead of an icon. */
  avatar?: boolean;
}

/** Top-nav links for the public marketing/site chrome. Live TV intentionally
 *  excluded — its entry point lives in the dashboard right-rail Live-TV CTA
 *  above the "Recently active" widget (see `RightRailWidgets.tsx`). */
export const publicNav: NavItem[] = [
  { label: 'Users', path: '/users', icon: Users },
  { label: 'Blogs', path: '/blogs', icon: PenSquare },
  { label: 'Places', path: '/places', icon: MapPin },
  { label: 'News', path: '/news', icon: Newspaper },
];

// `/about.html` link is hidden from the footer for now — the route still
// renders if linked from elsewhere. Re-add `{ label: 'About', path: '/about.html' }`
// when the About page is ready to surface again.
export const footerLinks: FooterLink[] = [
  { label: 'Pricing', path: '/pricing.html' },
  { label: 'Privacy', path: '/privacy-policy.html' },
  { label: 'Terms', path: '/terms-conditions.html' },
  { label: 'Refunds', path: '/refund-policy.html' },
  { label: 'Contact', path: '/contact-us.html' },
];

/** Footer social icons (hrefs are placeholders for now). */
export const footerSocials: SocialLink[] = [
  { label: 'Instagram', icon: Camera, href: '#' },
  { label: 'Facebook', icon: Globe, href: '#' },
  { label: 'YouTube', icon: PlayCircle, href: '#' },
];

/** Primary dashboard sidebar navigation. */
export const dashboardNav: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutGrid },
  { label: 'Messages', path: '/dashboard/messages', icon: Mail, badge: 'messages' },
  {
    label: 'Notifications',
    path: '/dashboard/notifications',
    icon: Bell,
    badge: 'notifications',
  },
  { label: 'My Blogs', path: '/dashboard/blogs', icon: PenSquare },
  { label: 'Friends', path: '/dashboard/friends', icon: Users, badge: 'requests' },
  {
    label: 'Profile Visitors',
    path: '/dashboard/visitors',
    icon: UserCircle,
    badge: 'visitors',
  },
  { label: 'Invite Friends', path: '/dashboard/invite-friends', icon: UserPlus },
  { label: 'People', path: '/users', icon: Users },
  { label: 'Places', path: '/places', icon: MapPin },
];

/**
 * User dropdown menu (avatar) items.
 *
 * The first entry (Dashboard) is rendered BEFORE the inline "Subscription"
 * action in `UserMenu.tsx`; the remaining entries render after it. Change
 * Password was removed — it lives on the Settings page already, so the
 * dropdown stays compact.
 */
export const userMenu: ActionItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutGrid },
  { label: 'Edit Profile', path: '/profile/edit', icon: Pencil },
  { label: 'Settings', path: '/dashboard/settings', icon: Settings },
  { label: 'Logout', action: 'logout', icon: LogOut },
];

/** Mobile bottom navigation — 6 uniform link slots (no raised center). */
export const mobileBottomNav: MobileNavItem[] = [
  { label: 'Home', path: '/dashboard', icon: LayoutGrid },
  { label: 'Messages', path: '/dashboard/messages', icon: Mail, badge: 'messages' },
  // Replaces the old raised "Create" plus-button — Search links to the
  // People listing page (2026-06-08).
  { label: 'Search', path: '/users', icon: Search },
  { label: 'Friends', path: '/dashboard/friends', icon: Users, badge: 'requests' },
  { label: 'Places', path: '/places', icon: MapPin },
  { label: 'Profile', path: '/dashboard/settings', icon: UserCircle, avatar: true },
];
